<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { api } from '@/api';
import type { HealthResponse } from '@klever-webhook/shared';

const health = ref<HealthResponse | null>(null);
const loading = ref(true);
const error = ref('');

async function loadHealth() {
  try {
    health.value = await api.getHealth();
  } catch (e) {
    error.value = 'Échec du chargement de l\'état du système';
  } finally {
    loading.value = false;
  }
}

onMounted(loadHealth);
</script>

<template>
  <div class="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
    <div class="px-4 py-6 sm:px-0">
      <h1 class="text-2xl font-semibold text-gray-900 mb-6">Tableau de bord</h1>

      <div v-if="loading" class="text-gray-500">Chargement...</div>
      <div v-else-if="error" class="text-red-600">{{ error }}</div>
      <div v-else-if="health" class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <!-- Health Status -->
        <div class="bg-white overflow-hidden shadow rounded-lg">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div
                  class="h-10 w-10 rounded-full flex items-center justify-center"
                  :class="{
                    'bg-green-100': health.status === 'healthy',
                    'bg-yellow-100': health.status === 'degraded',
                    'bg-red-100': health.status === 'unhealthy'
                  }"
                >
                  <span
                    class="text-lg"
                    :class="{
                      'text-green-600': health.status === 'healthy',
                      'text-yellow-600': health.status === 'degraded',
                      'text-red-600': health.status === 'unhealthy'
                    }"
                  >
                    {{ health.status === 'healthy' ? '✓' : health.status === 'degraded' ? '!' : '✗' }}
                  </span>
                </div>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-gray-500 truncate">État du système</dt>
                  <dd class="text-lg font-semibold text-gray-900 capitalize">{{ health.status === 'healthy' ? 'Opérationnel' : health.status === 'degraded' ? 'Dégradé' : 'Hors service' }}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <!-- Database Status -->
        <div class="bg-white overflow-hidden shadow rounded-lg">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div
                  class="h-10 w-10 rounded-full flex items-center justify-center"
                  :class="health.database ? 'bg-green-100' : 'bg-red-100'"
                >
                  <span :class="health.database ? 'text-green-600' : 'text-red-600'">
                    {{ health.database ? '✓' : '✗' }}
                  </span>
                </div>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-gray-500 truncate">Base de données</dt>
                  <dd class="text-lg font-semibold text-gray-900">
                    {{ health.database ? 'Connectée' : 'Déconnectée' }}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <!-- Webhooks 24h -->
        <div class="bg-white overflow-hidden shadow rounded-lg">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div class="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <span class="text-blue-600 text-lg">📨</span>
                </div>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-gray-500 truncate">Webhooks (24h)</dt>
                  <dd class="text-lg font-semibold text-gray-900">{{ health.stats.totalWebhooks24h }}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <!-- Success Rate -->
        <div class="bg-white overflow-hidden shadow rounded-lg">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div
                  class="h-10 w-10 rounded-full flex items-center justify-center"
                  :class="health.stats.successRate24h >= 95 ? 'bg-green-100' : health.stats.successRate24h >= 80 ? 'bg-yellow-100' : 'bg-red-100'"
                >
                  <span
                    :class="health.stats.successRate24h >= 95 ? 'text-green-600' : health.stats.successRate24h >= 80 ? 'text-yellow-600' : 'text-red-600'"
                  >
                    %
                  </span>
                </div>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-gray-500 truncate">Taux de réussite (24h)</dt>
                  <dd class="text-lg font-semibold text-gray-900">{{ health.stats.successRate24h.toFixed(1) }}%</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
